import { Component, DestroyRef, OnInit } from '@angular/core';
import { CalendarOptions, EventInput, DateSelectArg, EventClickArg } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { MatDialog } from '@angular/material/dialog';
import { EventDialogComponent } from '../../event-dialog/event-dialog.component';

import { ScheduleService } from 'src/app/_service/schedule.service';
import { TranslateService } from '@ngx-translate/core';

import { startWith } from 'rxjs/operators';

import { Router } from '@angular/router';
import * as routing from '../../../../_model/routing'

import jaLocale from '@fullcalendar/core/locales/ja';
import enLocale from '@fullcalendar/core/locales/en-gb';
// import enLocale from '@fullcalendar/core/locales-all';
@Component({
  selector: 'app-calendar',
  standalone: false,
  templateUrl: './calendar.component.html',
  styleUrl: './calendar.component.css'
})
export class CalendarComponent implements OnInit {
  allEvents: any[] = [];
  futureEvents: any[] = [];
  include_past: boolean = false;

  constructor(
    public dialog: MatDialog,
    private router: Router,
    private scheduleService: ScheduleService,
    private translateService: TranslateService,
    private destroyRef: DestroyRef,
  ) {}

  ngOnInit() {
    const storedValue = localStorage.getItem("include_past");
    this.include_past = storedValue ? JSON.parse(storedValue) : false;
    this.getEvents();

    // this.setCalendarOptions();

    const langChangeSub = this.translateService.onLangChange
    .pipe(
      startWith(null),
    )
    .subscribe(() => {
      this.setCalendarOptions();
    });

    this.destroyRef.onDestroy(() => langChangeSub.unsubscribe());
  }

  getEvents() {
    this.scheduleService.getSchedule(true).subscribe({
      next: (value: any) => {
        this.updateCalendarEvents(value?.data);
      },
      complete: () => {}
    });
  }

  onIncludePastChange(event: any) {
    this.include_past = (event.target as HTMLInputElement).checked;
    localStorage.setItem("include_past", JSON.stringify(this.include_past));

    if (this.calendarOptions) {
      this.calendarOptions.events = this.include_past ? this.allEvents : this.futureEvents;
    }
  }

  addEvent() {
    const dialogRef = this.dialog.open(EventDialogComponent, {
      width: '500px',
      height: '780px',
    });

    dialogRef.afterClosed().subscribe(e => {
      this.getEvents();
    });
  }

  calendarOptions: CalendarOptions = {
    initialView: 'timeGridWeek',
    headerToolbar: {
      left: 'prev,today,next',
      center: 'title',
      right: 'timeGridDay,timeGridWeek,dayGridMonth'
    },
    plugins: [interactionPlugin, dayGridPlugin, timeGridPlugin],
    weekends: true,
    selectable: true,
    editable: true,
    nowIndicator: true,
    scrollTime: this.getCurrentScrollTime(),
    eventBackgroundColor: "#b3e5fc",
    slotLabelFormat: {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    },
    eventTimeFormat: {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    },
    events: [],
    select: this.handleDateSelect.bind(this),
    eventClick: this.handleEventClick.bind(this),
    datesSet: this.checkPrevLimit.bind(this),

    slotMinTime: "07:00:00",
    slotMaxTime: "20:00:00",
  };

  setCalendarOptions() {
    const langConfig = this.getLangConfig(this.translateService.currentLang);
    const isJapanese = this.translateService.currentLang === 'jp';

    this.calendarOptions = {
      ...this.calendarOptions,
      buttonText: {
        today: this.translateService.instant('today'),
        day: this.translateService.instant('day'),
        week: this.translateService.instant('week'),
        month: this.translateService.instant('month'),
      },

      locale: langConfig.locale,
      views: {
        timeGridDay: isJapanese
          ? {}
          : {
            titleFormat: (dateInfo: any) => this.formatDayTitle(dateInfo, langConfig.dateLocale)
        },
        timeGridWeek: {
          titleFormat: (dateInfo: any) => this.formatWeekTitle(dateInfo, langConfig.dateLocale)
        }
      },
    }
  }

  private getLangConfig(lang: string): { locale: any; dateLocale: string } {
    switch (lang) {
      case 'jp':
        return {
          locale: {
            ...jaLocale,
            allDayText: '全日',
          },
          dateLocale: 'ja-JP'
        };
      default:
        return {
          locale: enLocale,
          dateLocale: 'en-US',
        }
    }
  }
  private formatDayTitle(dateInfo: any, dateLocale: string): string {
    const date = dateInfo.date.marker;
    if (dateLocale === 'ja-JP') {
      return this.formatYYYYMMDD(date);
    }

    const month = date.toLocaleDateString(dateLocale, { month: 'long'});
    const day = date.getDate();
    const year = date.getFullYear();

    return `${month} ${day}, ${year}`;
  }

  private formatWeekTitle(dateInfo: any, dateLocale: string): string {
    const start = dateInfo.start.marker;
    const end = dateInfo.end?.marker;

    if (dateLocale === 'ja-JP') {
      const startStr = this.formatYYYYMMDD(start);
      const endStr = this.formatYYYYMMDD(end);
      return `${startStr} - ${endStr}`;

    }

    const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric'};
    const startStr = start.toLocaleDateString(dateLocale, options);
    const endStr = end?.toLocaleDateString(dateLocale, {...options, year: "numeric"});
    return `${startStr} - ${endStr}`;
  }

  private formatYYYYMMDD(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}/${month}/${day}`;
  }


  checkPrevLimit(info: any) {
    const minDate = new Date();
    minDate.setDate(minDate.getDate() - 30);

    const startDate = new Date(info.start);

    const prevButton = document.querySelector('.fc-prev-button') as HTMLButtonElement;
    if (startDate < minDate) {
      prevButton.disabled = true;
      prevButton.classList.add('fc-disabled');
    } else {
      prevButton.disabled = false;
      prevButton.classList.remove('fc-disabled');
    }
  }

  getCurrentScrollTime(): string {
    const now = new Date();
    const scrollTime = new Date();

    scrollTime.setHours(now.getHours() - 1, now.getMinutes(), 0, 0);

    if (scrollTime.getDate() != now.getDate())
      scrollTime.setHours(0, 0, 0, 0);

    const hours = scrollTime.getHours().toString().padStart(2, '0');
    const minutes = scrollTime.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}:00`;
  }

  updateCalendarEvents(events: any) {
    this.allEvents = events.map((e: any) => {
      const startTime = e.scheduled_time;
      const endTimeDate = new Date(startTime);
      endTimeDate.setMinutes(endTimeDate.getMinutes() + e.scheduled_duration);
      const endTime = endTimeDate.toISOString();

      return {
        title: e.call_title,
        start: startTime,
        end: endTime,
        id: e.id,
        incident_details: e.incident_details,
        vessel_id: e.vessel_id,
        product_id: e.product_id,
      }
    });

    const now = new Date().toISOString();
    this.futureEvents = this.allEvents.filter(e => e.end >= now);

    if (this.calendarOptions) {
      this.calendarOptions.events = this.include_past ? this.allEvents : this.futureEvents;
    }
  }

  handleDateSelect(selectInfo: DateSelectArg) {
    const title = prompt('Please enter a new title for your event');
    const calendarApi = selectInfo.view.calendar;

    calendarApi.unselect();

    if (title) {
      calendarApi.addEvent({
        title,
        start: selectInfo.startStr,
        end: selectInfo.endStr,
        allDay: selectInfo.allDay
      });
    }
  }

  handleEventClick(clickInfo: EventClickArg) {
    const dialogRef = this.dialog.open(EventDialogComponent, {
      width: '500px',
      height: '835px',
      data: clickInfo.event,
    });

    console.log(clickInfo.event)

    dialogRef.afterClosed().subscribe(e => {
      this.getEvents();
    });
  }

  joinRoom(roomName: any) {
    this.router.navigate([routing.Meetings], { queryParams: { roomName: roomName }});
  }

  goJoin() {
    this.router.navigate([routing.Meetings]);
  }

}
